import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import {
  BarChart3,
  CalendarClock,
  FileSpreadsheet,
  Inbox,
  LayoutDashboard,
  LogOut,
  Menu,
  MessagesSquare,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  Sun,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useState, type ComponentType } from 'react';
import { useAuth, type Permission } from '../lib/auth';
import { cn } from '../lib/cn';
import { LANGUAGE_NAMES, PORTAL_LANGUAGES, useI18n, type PortalLanguage } from '../lib/i18n';
import { usePreferences, useSyncPreferences } from '../lib/preferences';
import type { ThemePreference } from '../lib/theme';
import { useOpenEscalations } from '../pages/worklist';
import { Button } from './ui/button';

// Coque du portail (ADR-023) : menu latéral repliable en barre d'icônes sur grand écran,
// tiroir sur mobile ; en-tête avec langue, thème et compte. Les préférences du compte sont
// appliquées à l'ouverture de session.

type NavKey =
  | 'dashboard'
  | 'installments'
  | 'portfolio'
  | 'scenarios'
  | 'worklist'
  | 'imports'
  | 'reports'
  | 'cash'
  | 'settings';

const NAV: {
  key: NavKey;
  to: string;
  icon: ComponentType<{ className?: string }>;
  permission?: Permission;
}[] = [
  { key: 'dashboard', to: '/', icon: LayoutDashboard },
  { key: 'installments', to: '/echeances', icon: CalendarClock },
  { key: 'portfolio', to: '/portefeuille', icon: Users },
  { key: 'scenarios', to: '/scenarios', icon: MessagesSquare },
  { key: 'worklist', to: '/file-de-travail', icon: Inbox },
  { key: 'imports', to: '/imports', icon: FileSpreadsheet },
  { key: 'reports', to: '/rapports', icon: BarChart3 },
  { key: 'cash', to: '/caisse', icon: Wallet, permission: 'recordPayment' },
  { key: 'settings', to: '/parametres', icon: Settings },
];

const THEME_ICONS: Record<ThemePreference, ComponentType<{ className?: string }>> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};
const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

function Brand({ collapsed }: { collapsed: boolean }) {
  const t = useI18n().t;
  return (
    <div
      className={cn('flex h-16 items-center gap-3 px-4', collapsed && 'md:justify-center md:px-0')}
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm">
        <ShieldCheck className="h-5 w-5" aria-hidden />
      </span>
      <span
        className={cn(
          'text-base font-semibold tracking-tight text-white',
          collapsed && 'md:sr-only',
        )}
      >
        {t.appName}
      </span>
    </div>
  );
}

export function AppShell() {
  const { t } = useI18n();
  const auth = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { preferences, update } = usePreferences();
  const me = useSyncPreferences();
  const user = auth.state.user;
  const openEscalations = useOpenEscalations();
  const collapsed = preferences.sidebarCollapsed;
  const ThemeIcon = THEME_ICONS[preferences.theme];

  async function logout() {
    await auth.logout();
    await navigate({ to: '/connexion' });
  }

  return (
    <div className="min-h-screen md:flex">
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded-lg focus:bg-card focus:p-2"
      >
        {t.nav.skipToContent}
      </a>
      {mobileOpen ? (
        <button
          type="button"
          aria-label={t.common.close}
          className="fixed inset-0 z-30 bg-overlay md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <aside
        className={cn(
          'fixed inset-y-0 start-0 z-40 flex w-64 flex-col bg-sidebar text-sidebar-foreground transition-[width,transform] duration-200 md:sticky md:top-0 md:h-screen',
          !mobileOpen && 'max-md:-translate-x-full max-md:rtl:translate-x-full',
          collapsed ? 'md:w-[72px]' : 'md:w-64',
        )}
        aria-label={t.nav.label}
      >
        <div className="flex items-center justify-between">
          <Brand collapsed={collapsed} />
          <button
            type="button"
            className="me-3 rounded-lg p-2 text-sidebar-muted hover:bg-sidebar-active hover:text-white md:hidden"
            aria-label={t.common.close}
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <nav aria-label={t.nav.label} className="flex-1 overflow-y-auto px-3 py-2">
          <ul className="flex flex-col gap-1">
            {NAV.filter((n) => !n.permission || auth.can(n.permission)).map(
              ({ key, to, icon: Icon }) => (
                <li key={key}>
                  <Link
                    to={to}
                    title={collapsed ? t.nav[key] : undefined}
                    onClick={() => setMobileOpen(false)}
                    activeOptions={{ exact: to === '/' }}
                    className={cn(
                      'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-active hover:text-white',
                      collapsed && 'md:justify-center md:px-0',
                    )}
                    activeProps={{
                      className: 'bg-sidebar-active text-sidebar-active-foreground',
                      'aria-current': 'page',
                    }}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                    <span className={cn('truncate', collapsed && 'md:sr-only')}>{t.nav[key]}</span>
                    {key === 'worklist' && openEscalations > 0 ? (
                      <span
                        className={cn(
                          'ms-auto rounded-full bg-destructive px-2 text-xs font-semibold text-white dark:text-background',
                          collapsed && 'md:absolute md:-top-0.5 md:end-1 md:ms-0 md:px-1.5',
                        )}
                        aria-label={`${openEscalations}`}
                      >
                        {openEscalations}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ),
            )}
          </ul>
        </nav>
        <div className="hidden border-t border-sidebar-border p-3 md:block">
          <button
            type="button"
            onClick={() => void update({ sidebarCollapsed: !collapsed })}
            aria-label={collapsed ? t.nav.expand : t.nav.collapse}
            aria-expanded={!collapsed}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-sidebar-muted hover:bg-sidebar-active hover:text-white',
              collapsed && 'justify-center px-0',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[18px] w-[18px] rtl:-scale-x-100" aria-hidden />
            ) : (
              <PanelLeftClose className="h-[18px] w-[18px] rtl:-scale-x-100" aria-hidden />
            )}
            <span className={cn(collapsed && 'sr-only')}>{t.nav.collapse}</span>
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-app-header
          className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-border bg-background/85 px-4 backdrop-blur md:px-6"
        >
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={t.nav.openMenu}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" aria-hidden />
          </Button>
          <span className="truncate text-sm font-medium text-muted-foreground">
            {me?.tenant.name ?? t.appName}
          </span>
          <div className="ms-auto flex items-center gap-1.5">
            <label className="sr-only" htmlFor="app-language">
              {t.common.language}
            </label>
            <select
              id="app-language"
              value={preferences.language}
              onChange={(e) => void update({ language: e.target.value as PortalLanguage })}
              className="h-9 rounded-lg border border-input bg-card px-2 text-sm text-card-foreground"
            >
              {PORTAL_LANGUAGES.map((l) => (
                <option key={l} value={l} lang={l}>
                  {LANGUAGE_NAMES[l]}
                </option>
              ))}
            </select>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`${t.theme.label} : ${t.theme[preferences.theme]}`}
              title={`${t.theme.label} : ${t.theme[preferences.theme]}`}
              onClick={() => void update({ theme: NEXT_THEME[preferences.theme] })}
            >
              <ThemeIcon className="h-[18px] w-[18px]" aria-hidden />
            </Button>
            {user ? (
              <Link
                to="/compte"
                className="ms-1 flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted"
                aria-label={t.account.title}
              >
                <span className="grid h-8 w-8 place-items-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
                  {initials(me?.name ?? user.name)}
                </span>
                <span className="hidden text-start leading-tight lg:block">
                  <span className="block text-sm font-medium">{me?.name ?? user.name}</span>
                  <span className="block text-xs text-muted-foreground">{t.roles[user.role]}</span>
                </span>
              </Link>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              aria-label={t.nav.logout}
              title={t.nav.logout}
              onClick={() => void logout()}
            >
              <LogOut className="h-[18px] w-[18px] rtl:-scale-x-100" aria-hidden />
            </Button>
          </div>
        </header>
        <main
          id="contenu"
          className="mx-auto w-full max-w-[1400px] min-w-0 flex-1 p-4 md:p-6 lg:p-8"
        >
          {user?.mfaSetupRequired ? (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-warning"
            >
              {t.mfa.required}{' '}
              <Link to="/parametres" search={{ tab: 'securite' }} className="font-medium underline">
                {t.settings.totpStart}
              </Link>
            </p>
          ) : null}
          {me?.tenant.subscriptionStatus &&
          ['past_due', 'suspended', 'cancelled'].includes(me.tenant.subscriptionStatus) ? (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-destructive/30 bg-destructive-soft p-3 text-sm text-destructive"
            >
              {
                t.billing.banner[
                  me.tenant.subscriptionStatus as 'past_due' | 'suspended' | 'cancelled'
                ]
              }{' '}
              {auth.can('administer') ? (
                <Link
                  to="/parametres"
                  search={{ tab: 'abonnement' }}
                  className="font-medium underline"
                >
                  {t.billing.bannerAction}
                </Link>
              ) : (
                t.billing.bannerContact
              )}
            </p>
          ) : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
